export const SKIN_SIZE = 64 as const;
export const MAX_SKIN_SIZE = 128 as const;
export const SUPPORTED_SKIN_SIZES = [SKIN_SIZE, MAX_SKIN_SIZE] as const;
export const SKIN_PIXEL_COUNT = SKIN_SIZE * SKIN_SIZE;
export const RGBA_CHANNELS = 4 as const;

export type SkinSize = (typeof SUPPORTED_SKIN_SIZES)[number];
export type SkinModel = "classic" | "slim";
export type SkinPart =
  | "head"
  | "body"
  | "rightArm"
  | "leftArm"
  | "rightLeg"
  | "leftLeg";
export type SkinLayer = "inner" | "outer";
export type SkinFace = "front" | "back" | "left" | "right" | "top" | "bottom";

export interface SkinDocument {
  pixels: Uint8ClampedArray;
  model: SkinModel;
  size: SkinSize;
  revision: number;
}

export interface SurfaceRef {
  part: SkinPart;
  layer: SkinLayer;
  face: SkinFace;
}

export interface PixelPoint {
  x: number;
  y: number;
}

export interface PixelRect extends PixelPoint {
  width: number;
  height: number;
}

export interface SurfacePoint extends PixelPoint {
  surface: SurfaceRef;
}

export interface SurfaceSize {
  width: number;
  height: number;
}

export type RgbaHex = `#${string}`;

export interface PixelOperation extends PixelPoint {
  type: "pixel";
  color: RgbaHex;
}

export interface RectOperation extends PixelRect {
  type: "rect";
  color: RgbaHex;
  /** Defaults to true. False draws a one-pixel outline. */
  filled?: boolean;
}

export interface LineOperation {
  type: "line";
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: RgbaHex;
}

export interface FloodFillOperation extends PixelPoint {
  type: "floodFill";
  color: RgbaHex;
}

export interface ReplaceOperation {
  type: "replace";
  from: RgbaHex;
  to: RgbaHex;
  region?: PixelRect;
}

export interface MirrorOperation {
  type: "mirror";
  axis: "horizontal" | "vertical";
  /** Mirrors one logical face using its external-observer coordinate system. */
  surface?: SurfaceRef;
  /** Mirrors a raw UV rectangle. Mutually exclusive with surface. */
  region?: PixelRect;
}

/** Draws one pixel in a face's local external-observer coordinates. */
export interface SurfacePixelOperation extends PixelPoint {
  type: "surfacePixel";
  surface: SurfaceRef;
  color: RgbaHex;
}

/** Draws a rectangle in face-local coordinates. */
export interface SurfaceRectOperation extends PixelRect {
  type: "surfaceRect";
  surface: SurfaceRef;
  color: RgbaHex;
  filled?: boolean;
}

/** Draws a line in face-local coordinates. */
export interface SurfaceLineOperation {
  type: "surfaceLine";
  surface: SurfaceRef;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: RgbaHex;
}

/** Flood-fills within one face only (does not spill across UV faces). */
export interface SurfaceFloodFillOperation extends PixelPoint {
  type: "surfaceFloodFill";
  surface: SurfaceRef;
  color: RgbaHex;
}

/** Replaces a color on one face, optionally limited to a local rect. */
export interface SurfaceReplaceOperation {
  type: "surfaceReplace";
  surface: SurfaceRef;
  from: RgbaHex;
  to: RgbaHex;
  region?: PixelRect;
}

/** Copies one face onto another of the same size, with optional flips. */
export interface CopySurfaceOperation {
  type: "copySurface";
  from: SurfaceRef;
  to: SurfaceRef;
  flipHorizontal?: boolean;
  flipVertical?: boolean;
}

/**
 * Copies every face of one body part onto another.
 * For left/right limb pairs, left↔right faces are remapped and each face is
 * flipped horizontally by default so the result looks mirrored.
 */
export interface CopyPartOperation {
  type: "copyPart";
  fromPart: SkinPart;
  toPart: SkinPart;
  /** Defaults to both layers. */
  layers?: "inner" | "outer" | "both";
  /** Defaults to true when copying between left/right counterparts. */
  flipHorizontal?: boolean;
}

export type SkinOperation =
  | PixelOperation
  | RectOperation
  | LineOperation
  | FloodFillOperation
  | ReplaceOperation
  | MirrorOperation
  | SurfacePixelOperation
  | SurfaceRectOperation
  | SurfaceLineOperation
  | SurfaceFloodFillOperation
  | SurfaceReplaceOperation
  | CopySurfaceOperation
  | CopyPartOperation;

export type DrawOrigin = "manual" | "ai";

export const SKIN_PARTS: readonly SkinPart[] = [
  "head",
  "body",
  "rightArm",
  "leftArm",
  "rightLeg",
  "leftLeg",
];

export const SKIN_LAYERS: readonly SkinLayer[] = ["inner", "outer"];
export const SKIN_FACES: readonly SkinFace[] = [
  "front",
  "back",
  "left",
  "right",
  "top",
  "bottom",
];
